import { InputProps } from './input-prop.interface';
import { InputState } from './input-state.enum';
import { InputType } from './input-type.enum';
import { InteractiveMouseEvent } from './interactive-mouse-event.enum';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import CircleXmark from 'src/assets/images/icons/default/CircleXmark';
import DisabledEye from 'src/assets/images/icons/default/DisabledEye';
import Eye from 'src/assets/images/icons/default/Eye';
import EyeSlash from 'src/assets/images/icons/default/EyeSlash';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

/**
 * Modern Input Component
 *
 * A beautifully designed input component with glassmorphism effects, soft gradients, and smooth animations.
 * Features modern design patterns with pastel colors and clean typography to match the overall website aesthetic.
 */
const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      state,
      error = false,
      elemBefore,
      elemAfter,
      elemBeforeMessage,
      message,
      messageClassName,
      placeholder,
      id,
      name,
      autoComplete,
      inputType = InputType.Text,
      password = false,
      value: initialValue = '',
      inputPattern = undefined,
      maxValue = undefined,
      className,
      hideClear = false,
      onChange,
      onClear,
      onKeyDown,
      maximumLength = null,
      inputClassName,
    },
    ref
  ) => {
    // State to manage the input's current value
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    // State to toggle password visibility
    const [showPassword, setShowPassword] = useState(false);

    // State to manage various interaction states of the input
    const [inputState, setInputState] = useState({
      isHovered: false, // Indicates if the input is hovered
      isFocused: false, // Indicates if the input is focused
      isEntering: false, // Indicates if the mouse is entering the input area
      isLeaving: false, // Indicates if the mouse is leaving the input area
    });
    const t = useTranslationByKey('COMPONENTS.INPUT');
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    /**
     * Handler for input value changes.
     * Updates the local state and calls the onChange prop if provided.
     */
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let newValue = e.target.value;
        if (inputPattern) {
          newValue = newValue.replace(inputPattern, '');
        }

        if (maxValue) {
          const numberValue = Number(newValue);
          if (!isNaN(numberValue) && numberValue > maxValue) {
            newValue = maxValue.toString();
          }
        }

        setValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      },
      [onChange, inputPattern, maxValue]
    );

    /**
     * Handler for mouse enter and leave events.
     * Updates the inputState based on the type of mouse event.
     */
    const handleMouseEvents = useCallback(
      (event: InteractiveMouseEvent) => {
        if (error || state === InputState.Disabled) {
          return;
        }
        if (event === InteractiveMouseEvent.Enter) {
          setInputState((prev) => ({ ...prev, isHovered: true, isEntering: true }));
        } else if (event === InteractiveMouseEvent.Leave) {
          setInputState((prev) => ({ ...prev, isHovered: false, isEntering: false, isLeaving: true }));
        }
      },
      [error, state]
    );

    /**
     * Get modern glassmorphism and state-based styles
     */
    const getInputWrapperStyles = () => {
      const baseStyles = [
        'w-full',
        'self-stretch',
        'p-3',
        'rounded-2xl',
        'border',
        'justify-start',
        'gap-2',
        'flex',
        'transition-all',
        'duration-300',
        'ease-out',
        'backdrop-blur-sm',
        'relative',
        'z-0',
        'overflow-hidden',
        'focus-within:ring-2',
        'focus-within:ring-inset',
        'focus-within:ring-offset-0',
        'focus-within:z-10',
      ];

      // Height based on input type
      if (inputType === InputType.TextArea) {
        baseStyles.push('h-[88px]');
      } else {
        baseStyles.push('h-11', 'items-center');
      }

      // State-based styling
      if (state === InputState.Disabled) {
        baseStyles.push(
          'bg-gray-50/80',
          'border-gray-200/50',
          'text-gray-400',
          'cursor-not-allowed',
          'backdrop-blur-sm'
        );
      } else if (error) {
        baseStyles.push(
          'bg-red-50/80',
          'border-red-200/50',
          'shadow-sm',
          'shadow-red-100/50',
          'focus-within:ring-red-200/50'
        );

        if (inputState.isFocused) {
          baseStyles.push('border-red-300/70', 'shadow-md', 'shadow-red-200/50');
        }

        if (inputState.isHovered) {
          baseStyles.push('bg-red-50/90', 'border-red-300/60', 'shadow-md', 'shadow-red-100/60');
        }
      } else {
        baseStyles.push(
          'bg-white/60',
          'border-gray-200/50',
          'shadow-sm',
          'shadow-gray-100/50',
          'focus-within:ring-indigo-200/50'
        );

        if (inputState.isFocused) {
          baseStyles.push('bg-white/80', 'border-indigo-300/70', 'shadow-md', 'shadow-indigo-200/50');
        }

        if (inputState.isHovered && !inputState.isFocused) {
          baseStyles.push('bg-white/70', 'border-gray-300/60', 'shadow-md', 'shadow-gray-200/50');
        }
      }

      return baseStyles;
    };

    /**
     * Get modern input field styles
     */
    const getInputFieldStyles = () => {
      const baseStyles = [
        'input-field',
        'outline-none',
        'w-full',
        'bg-transparent',
        'text-sm',
        'font-spaceGrotesk',
        'placeholder:font-spaceGrotesk',
        'placeholder:text-sm',
        'placeholder:tracking-normal',
        'transition-all',
        'duration-200',
        'ease-out',
      ];

      if (password) {
        baseStyles.push('pr-2');
      }

      if (inputType === InputType.TextArea) {
        baseStyles.push('h-full');
      }

      // State-based text colors
      if (state === InputState.Disabled) {
        baseStyles.push('text-gray-400', 'placeholder:text-gray-300');
      } else if (error) {
        baseStyles.push('text-red-700', 'placeholder:text-red-400');
      } else {
        baseStyles.push('text-gray-700', 'placeholder:text-gray-500');
      }

      return baseStyles;
    };

    /**
     * Handler for animation end events.
     * Resets the `isLeaving` state after the leave animation completes.
     */
    const handleAnimationEnd = useCallback(() => {
      if (inputState.isLeaving) {
        setInputState((prev) => ({ ...prev, isLeaving: false }));
      }
    }, [inputState.isLeaving]);

    const stateRef = useRef(state);
    /**
     * Handler for input focus and blur events.
     * Updates the `isFocused` state accordingly.
     */
    const handleInputFocus = useCallback((focused: boolean) => {
      setInputState((prev) => ({ ...prev, isFocused: focused }));
      if (focused && stateRef.current !== InputState.Disabled) {
        stateRef.current = InputState.Activated; // Set active when focused
      }
    }, []);

    const inputWrapperClasses = classNames(...getInputWrapperStyles(), inputClassName);
    const inputClasses = classNames(...getInputFieldStyles());

    return (
      <div className={classNames('flex w-full flex-col justify-center items-start', className)}>
        <div
          className={inputWrapperClasses}
          onMouseEnter={() => handleMouseEvents(InteractiveMouseEvent.Enter)}
          onMouseLeave={() => handleMouseEvents(InteractiveMouseEvent.Leave)}
          onAnimationEnd={handleAnimationEnd}
        >
          {/* Subtle shimmer effect on hover */}
          {!error && state !== InputState.Disabled && (
            <div
              className={classNames(
                'absolute inset-0 opacity-0 bg-gradient-to-r from-transparent via-white/20 to-transparent',
                'transition-opacity duration-300 pointer-events-none',
                inputState.isHovered && 'opacity-100'
              )}
            />
          )}

          {elemBefore && <div className="relative z-10">{elemBefore}</div>}

          <div className={classNames('w-full relative flex z-10', inputType === InputType.TextArea && 'h-full')}>
            {inputType === InputType.TextArea ? (
              <textarea
                ref={(element) => {
                  inputRef.current = element;
                  if (typeof ref === 'function') {
                    ref(element as HTMLTextAreaElement);
                  } else if (ref) {
                    (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = element;
                  }
                }}
                className={inputClasses}
                id={id}
                name={name}
                autoComplete={autoComplete}
                value={value}
                onChange={handleInputChange}
                onFocus={() => handleInputFocus(true)}
                onBlur={() => handleInputFocus(false)}
                onKeyDown={onKeyDown}
                disabled={state === InputState.Disabled}
                placeholder={value ? '' : placeholder}
                maxLength={maximumLength ?? undefined}
              />
            ) : (
              <input
                ref={(element) => {
                  inputRef.current = element;
                  if (typeof ref === 'function') {
                    ref(element);
                  } else if (ref) {
                    ref.current = element;
                  }
                }}
                className={inputClasses}
                id={id}
                name={name}
                autoComplete={autoComplete}
                value={value}
                type={password ? (showPassword ? 'text' : 'password') : inputType || 'text'}
                onChange={handleInputChange}
                onFocus={() => handleInputFocus(true)}
                onBlur={() => handleInputFocus(false)}
                onKeyDown={onKeyDown}
                disabled={state === InputState.Disabled}
                placeholder={value ? '' : placeholder}
                maxLength={maximumLength ?? undefined}
              />
            )}

            {password && (
              <button
                type="button"
                onClick={() => state !== InputState.Disabled && setShowPassword(!showPassword)}
                className={classNames(
                  'w-5 h-5 flex justify-center items-center transition-all duration-200',
                  state === InputState.Disabled ? 'cursor-not-allowed' : 'hover:scale-110'
                )}
              >
                {state === InputState.Disabled ? (
                  <DisabledEye className="w-5 h-5 text-gray-400" />
                ) : showPassword ? (
                  <Eye className="w-5 h-5 text-gray-600 hover:text-gray-700" />
                ) : (
                  <EyeSlash className="w-5 h-5 text-gray-600 hover:text-gray-700" />
                )}
              </button>
            )}

            {!hideClear &&
              !password &&
              state !== InputState.Disabled &&
              value != '' &&
              inputType !== InputType.TextArea && (
                <button
                  type="button"
                  onClick={() => {
                    setValue(''); // Clear the input value
                    inputRef.current?.focus();
                    if (onClear) {
                      onClear(); // Call the onClear function if provided
                    }
                  }}
                  className="w-5 h-5 flex justify-center items-center transition-all duration-200 hover:scale-110"
                >
                  <CircleXmark className="w-[15px] h-[15px] text-gray-500 hover:text-gray-700" />
                </button>
              )}
          </div>

          {elemAfter && <div className="ml-2 relative z-10">{elemAfter}</div>}
        </div>

        {(elemBeforeMessage || message) && (
          <div className="w-full relative flex items-center gap-1 pt-1.5">
            {elemBeforeMessage &&
              (typeof elemBeforeMessage === 'string' ? (
                <img src={elemBeforeMessage} alt={t('TEXT_ICON')} className="w-4 h-4" />
              ) : (
                elemBeforeMessage // Render directly if it's an element
              ))}
            {message && (
              <div
                className={classNames(
                  'font-spaceGrotesk input-message text-sm font-medium flex items-center',
                  error ? 'text-red-600' : 'text-gray-600',
                  messageClassName
                )}
              >
                {message}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export default Input;
